/**
 * PaperJournalUploadModal — REW-929 Phase 3.
 *
 * Two-state modal:
 *   - Picker state: file input (.csv only) + Upload button. Errors are
 *     status-aware (409 duplicate / 400 validation / 500 server / network).
 *   - Result state: per-row outcome list from the POST response, plus a
 *     "Done" button that dismisses the result and closes the modal.
 *
 * Upload triggering and refetch live in usePaperJournalData; the modal is
 * presentational beyond capturing the chosen file and calling uploadPaperJournal.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal, Button } from '@/components/common/Modal';
import { paperJournalUploadPath } from '@/pages/PaperJournal/paperJournalAdminLinks';
import type {
  PaperJournalRowOutcome,
  PaperJournalUploadResponse,
} from '@/app/types/paperJournal';
import type { PaperJournalUploadError } from '@/app/store/paperJournalSlice';

import { PaperJournalCsvFormatGuide } from '@/pages/PaperJournal/PaperJournalCsvFormatGuide';

import './PaperJournalUploadModal.scss';

interface PaperJournalUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  uploadPaperJournal: (file: File) => Promise<{ ok: boolean }>;
  uploading: boolean;
  lastUploadResponse: PaperJournalUploadResponse | null;
  uploadError: PaperJournalUploadError | null;
  dismissUploadResult: () => void;
}

function _errorBlock(uploadError: PaperJournalUploadError): React.ReactNode {
  const status = uploadError.status;
  if (status === 409) {
    return (
      <div className="paper-journal-upload-modal__error">
        <div className="paper-journal-upload-modal__error-title">
          This file has already been uploaded
        </div>
        <div className="paper-journal-upload-modal__error-detail">
          {uploadError.message}
        </div>
        {uploadError.fileSha256 !== undefined && uploadError.fileSha256 !== '' && (
          <Link
            to={paperJournalUploadPath(uploadError.fileSha256)}
            className="paper-journal-upload-modal__error-link"
          >
            View existing upload rows
          </Link>
        )}
      </div>
    );
  }
  if (status === 400) {
    return (
      <div className="paper-journal-upload-modal__error">
        <div className="paper-journal-upload-modal__error-title">
          The file is invalid
        </div>
        <div className="paper-journal-upload-modal__error-detail">
          {uploadError.message}
        </div>
      </div>
    );
  }
  if (status === 500) {
    return (
      <div className="paper-journal-upload-modal__error">
        <div className="paper-journal-upload-modal__error-title">
          Server error — please try again
        </div>
        <div className="paper-journal-upload-modal__error-detail">
          {uploadError.message}
        </div>
      </div>
    );
  }
  return (
    <div className="paper-journal-upload-modal__error">
      <div className="paper-journal-upload-modal__error-title">Network error</div>
      <div className="paper-journal-upload-modal__error-detail">
        {uploadError.message}
      </div>
    </div>
  );
}

function _outcomeRow(row: PaperJournalRowOutcome): React.ReactNode {
  return (
    <li
      key={row.row_index}
      className={`paper-journal-upload-modal__row paper-journal-upload-modal__row--${row.outcome.toLowerCase()}`}
    >
      <span className="paper-journal-upload-modal__row-index">Row {row.row_index + 1}</span>
      <span className="paper-journal-upload-modal__row-outcome">{row.outcome}</span>
      {row.error !== null && (
        <span className="paper-journal-upload-modal__row-error">{row.error}</span>
      )}
    </li>
  );
}

export const PaperJournalUploadModal: React.FC<PaperJournalUploadModalProps> = ({
  isOpen,
  onClose,
  uploadPaperJournal,
  uploading,
  lastUploadResponse,
  uploadError,
  dismissUploadResult,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  if (!isOpen) {
    return null;
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (selectedFile === null) {
      return;
    }
    await uploadPaperJournal(selectedFile);
  };

  const handleDone = () => {
    dismissUploadResult();
    setSelectedFile(null);
    onClose();
  };

  const handleCancel = () => {
    setSelectedFile(null);
    onClose();
  };

  const isResultView = lastUploadResponse !== null;

  if (isResultView && lastUploadResponse !== null) {
    const footer = (
      <Button onClick={handleDone}>Done</Button>
    );
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleDone}
        title="Upload result"
        size="lg"
        footer={footer}
      >
        <div className="paper-journal-upload-modal__result">
          <div className="paper-journal-upload-modal__result-summary">
            <div className="paper-journal-upload-modal__result-stat">
              <span className="paper-journal-upload-modal__result-stat-label">Total rows</span>
              <span className="paper-journal-upload-modal__result-stat-value">
                {lastUploadResponse.row_count}
              </span>
            </div>
            <div className="paper-journal-upload-modal__result-stat">
              <span className="paper-journal-upload-modal__result-stat-label">Accepted</span>
              <span className="paper-journal-upload-modal__result-stat-value paper-journal-upload-modal__result-stat-value--accepted">
                {lastUploadResponse.accepted_count}
              </span>
            </div>
            <div className="paper-journal-upload-modal__result-stat">
              <span className="paper-journal-upload-modal__result-stat-label">Rejected</span>
              <span
                className={`paper-journal-upload-modal__result-stat-value${
                  lastUploadResponse.rejected_count > 0
                    ? ' paper-journal-upload-modal__result-stat-value--rejected'
                    : ''
                }`}
              >
                {lastUploadResponse.rejected_count}
              </span>
            </div>
          </div>
          <ul className="paper-journal-upload-modal__rows">
            {lastUploadResponse.rows.map(_outcomeRow)}
          </ul>
        </div>
      </Modal>
    );
  }

  const uploadDisabled = selectedFile === null || uploading;
  const footer = (
    <>
      <Button variant="secondary" onClick={handleCancel} disabled={uploading}>
        Cancel
      </Button>
      <Button onClick={handleUpload} disabled={uploadDisabled}>
        {uploading ? 'Uploading…' : 'Upload'}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Upload paper journal CSV"
      size="lg"
      footer={footer}
    >
      <PaperJournalCsvFormatGuide />
      <div className="paper-journal-upload-modal__picker">
        <label
          htmlFor="paper-journal-csv-file"
          className="paper-journal-upload-modal__label"
        >
          CSV file
        </label>
        <input
          id="paper-journal-csv-file"
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          disabled={uploading}
        />
        {selectedFile !== null && (
          <div className="paper-journal-upload-modal__filename">
            Selected: {selectedFile.name}
          </div>
        )}
        {uploadError !== null && _errorBlock(uploadError)}
      </div>
    </Modal>
  );
};

export default PaperJournalUploadModal;
